import { PgnParser } from "./PgnParser";
import { PgnDataBuilder } from "./PgnDataBuilder";
/**
 * Generates a canonical representation of moves for fingerprinting.
 * Includes only SAN notation and puzzle markers {[#]}, excluding all other annotations.
 *
 * @param pgn - The PGN string to process
 * @param chess - Chess instance for parsing
 * @returns Canonical moves string with only SAN and puzzle markers
 */
export function generateCanonicalMoves(pgn, chess) {
    try {
        const builder = new PgnDataBuilder(pgn, chess);
        const pgnData = builder.pgnData();
        if (!pgnData.rootMove) {
            return "";
        }
        return buildCanonicalMovesString(pgnData.rootMove, false);
    }
    catch (error) {
        // If parsing fails, return empty string
        return "";
    }
}
/**
 * Recursively builds canonical moves string from PgnMove tree
 *
 * @param move - Current move to process
 * @param isPuzzleMove - Whether we're in puzzle move territory
 * @returns Canonical moves string
 */
function buildCanonicalMovesString(move, isPuzzleMove) {
    if (!move)
        return "";
    let result = "";
    let updatedIsPuzzleMove = isPuzzleMove;
    // Add puzzle marker if this is the first puzzle move
    if (move.isPuzzleMove && !updatedIsPuzzleMove) {
        updatedIsPuzzleMove = true;
        result += "{[#]} ";
    }
    // Add move number if it's white's move or first move in a variation
    if (move.color === "w" || !move.previousMoveInline) {
        result += `${move.number}${move.color === "w" ? "." : "..."} `;
    }
    // Add only the SAN notation (no NAGs, comments, annotations, or user scores)
    result += move.san + " ";
    // Process variations (child moves)
    if (move.childMoves && move.childMoves.length > 0) {
        for (const childMove of move.childMoves) {
            result +=
                "(" +
                    buildCanonicalMovesString(childMove, updatedIsPuzzleMove).trim() +
                    ") ";
        }
    }
    // Continue with the next move in the main line
    if (move.nextMove) {
        result += buildCanonicalMovesString(move.nextMove, updatedIsPuzzleMove);
    }
    return result;
}
/**
 * Generates a SHA-256 fingerprint for a scenario based on its FEN and canonical moves.
 * Two scenarios with the same starting position, moves, and puzzle structure will have
 * the same fingerprint, regardless of metadata, comments, or annotations.
 *
 * @param pgn - The PGN string to fingerprint
 * @param chess - Chess instance for parsing
 * @returns SHA-256 hash as a 64-character hexadecimal string
 */
export async function generateScenarioFingerprint(pgn, chess) {
    // Extract normalized FEN
    const fen = PgnParser.extractInitialFen(pgn);
    // Generate canonical moves
    const moves = generateCanonicalMoves(pgn, chess);
    // Combine FEN and moves with a separator
    const input = `${fen}|${moves.trim()}`;
    // Generate SHA-256 hash
    return await sha256(input);
}
/**
 * Computes SHA-256 hash of a string.
 * Uses Web Crypto API in browsers or Node.js crypto module in Node environments.
 *
 * @param message - The string to hash
 * @returns SHA-256 hash as hexadecimal string
 */
async function sha256(message) {
    // Try Web Crypto API (browser and modern Node.js)
    if (typeof crypto !== "undefined" && crypto.subtle) {
        const msgBuffer = new TextEncoder().encode(message);
        const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
    // Fallback for Node.js without Web Crypto API
    // This will be handled by the bundler/environment
    try {
        // Dynamic import for Node.js crypto module
        const nodeCrypto = await import("crypto");
        return nodeCrypto.createHash("sha256").update(message).digest("hex");
    }
    catch (error) {
        // If all else fails, use a simple hash (not cryptographically secure)
        // This should never happen in practice
        console.warn("SHA-256 not available, using fallback hash");
        return simpleFallbackHash(message);
    }
}
/**
 * Simple fallback hash function (NOT cryptographically secure).
 * Only used if SHA-256 is not available in the environment.
 *
 * @param str - String to hash
 * @returns Hexadecimal hash string
 */
function simpleFallbackHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Pad to 64 characters to match SHA-256 length
    return Math.abs(hash).toString(16).padStart(64, "0");
}
